<template>
  <div class="flex flex-col gap-y-4">
    <div class="flex flex-col gap-y-3">
      <h2 class="text-white text-lg sm:text-xl md:text-2xl font-semibold">
        <Translation
          name="calculation_wizard.preview.sidebar_title"
          default="How are these numbers calculated?"
        />
      </h2>

      <div class="flex flex-col gap-y-1">
        <p class="text-base text-white mb-2">
          <Translation
            name="calculation_wizard.preview.sidebar_info"
            default="Add info text here"
          />
        </p>
        <p class="text-white text-opacity-70">
          <strong class="font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_job_category"
              default="Job category: "
            /> </strong
          >{{ selectedBar?.jobCategory || "-" }}
        </p>
        <p class="text-white text-opacity-70">
          <strong class="font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_gender"
              default="Gender: "
            /> </strong
          >{{ selectedBar?.gender || "-" }}
        </p>
      </div>
    </div>
    <div class="rounded-xl bg-[#3B3B44] p-1.5 flex items-center gap-x-1">
      <button
        class="tab-button"
        @click="changeTab('wages')"
        :class="tab === 'wages' ? 'active' : ''"
      >
        <Translation
          name="calculation_wizard.preview.sidebar_wages"
          default="Wages"
        />
      </button>
      <button
        class="tab-button"
        @click="changeTab('bonuses')"
        :class="tab === 'bonuses' ? 'active' : ''"
      >
        <Translation
          name="calculation_wizard.preview.sidebar_bonuses"
          default="Bonuses"
        />
      </button>
      <button
        class="tab-button"
        @click="changeTab('benefits')"
        :class="tab === 'benefits' ? 'active' : ''"
      >
        <Translation
          name="calculation_wizard.preview.sidebar_ikb"
          default="In-kind Benefits"
        />
      </button>
    </div>
    <div>
      <WagesTab
        v-if="tab === 'wages'"
        :wageEarned="Number(selectedBar?.wageEarned)"
        :currency="currency"
        :hoursWorked="Number(selectedBar?.hoursWorked)"
        :shareOfYearWorked="Number(selectedBar?.shareOfYearWorked)"
        :comparableAnnualWage="Number(selectedBar?.comparableAnnualWage)"
        :comparableMonthlyWage="Number(selectedBar?.comparableMonthlyWage)"
        :jobCategory="selectedBar?.jobCategory"
        :gender="selectedBar?.gender"
        :maxRegularWorkingHoursPerYear="
          Number(selectedBar?.maxRegularWorkingHoursPerYear)
        "
        :fullTimeWorkingHoursPerYear="
          Number(selectedBar?.fullTimeWorkingHoursPerYear)
        "
        :hoursPaidAnnualLeave="Number(selectedBar?.hoursPaidAnnualLeave)"
        :hoursUnpaidMandatoryLeave="
          Number(selectedBar?.hoursUnpaidMandatoryLeave)
        "
        :rawShareOfYearWorked="Number(selectedBar?.rawShareOfYearWorked)"
        :overtimeHoursExceeded="selectedBar?.overtimeHoursExceeded"
        :correctedWageEarned="Number(selectedBar?.correctedWageEarned)"
        :standardWorkweekHours="Number(selectedBar?.standardWorkweekHours)"
        :weeksPerYear="Number(selectedBar?.weeksPerYear)"
      />
      <BonusesTab
        v-if="tab === 'bonuses'"
        :currency="currency"
        :thirteenFourteenFifteenMonthPayment="Number(selectedBar?.bonus_1)"
        :timeDependentBonuses="Number(selectedBar?.bonus_2)"
        :nonTimeDependentBonuses="Number(selectedBar?.bonus_3)"
        :shareOfYearWorked="Number(selectedBar?.shareOfYearWorked)"
        :comparableAnnualTimeDependentBonuses="
          Number(selectedBar?.comparableAnnualTimeDependentBonuses)
        "
        :totalComparableAnnualBonuses="
          Number(selectedBar?.totalComparableAnnualBonuses)
        "
        :comparableMonthlyAverageBonuses="
          Number(selectedBar?.comparableMonthlyAverageBonuses)
        "
        :jobCategory="selectedBar?.jobCategory"
        :gender="selectedBar?.gender"
        :rawShareOfYearWorked="Number(selectedBar?.rawShareOfYearWorked)"
        :overtimeHoursExceeded="selectedBar?.overtimeHoursExceeded"
      />
      <BenefitsTab
        v-if="tab === 'benefits'"
        :currency="currency"
        :ikbHousing="Number(selectedBar?.ikb_housing)"
        :ikbChildcare="Number(selectedBar?.ikb_childcare)"
        :ikbChildEducation="Number(selectedBar?.ikb_child_education)"
        :ikbFood="Number(selectedBar?.ikb_food)"
        :ikbHealthcare="Number(selectedBar?.ikb_healthcare)"
        :ikbTransportation="Number(selectedBar?.ikb_transportation)"
        :shareOfYearWorked="Number(selectedBar?.shareOfYearWorked)"
        :comparableWageAndBonuses="
          Number(selectedBar?.comparableWageAndBonuses)
        "
        :comparableAnnualInKindBenefits="
          Number(selectedBar?.comparableAnnualInKindBenefits)
        "
        :comparableMonthlyInKindBenefits="
          Number(selectedBar?.comparableMonthlyInKindBenefits)
        "
        :capsApplied="selectedBar?.capsApplied"
        :capsExceeded="selectedBar?.capsExceeded"
        :combinedCapsExceeded="selectedBar?.combinedCapsExceeded"
        :housingIkbCapPercentage="Number(selectedBar?.housingIkbCapPercentage)"
        :otherIkbCapPercentage="Number(selectedBar?.otherIkbCapPercentage)"
        :totalIkbCapPercentage="Number(selectedBar?.totalIkbCapPercentage)"
        :jobCategory="selectedBar?.jobCategory"
        :gender="selectedBar?.gender"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import WagesTab from "./WagesTab.vue";
import BonusesTab from "./BonusesTab.vue";
import BenefitsTab from "./BenefitsTab.vue";

const tab = ref("wages");
const selectedBar = ref(null);
const currency = ref("");

const changeTab = (tabName) => {
  tab.value = tabName;
};

const handleChartBarSelected = (event) => {
  selectedBar.value = event.detail.bar;
  currency.value = event.detail.currency;
  tab.value = "wages";
};

onMounted(() => {
  document.addEventListener("chart-bar-selected", handleChartBarSelected);
});

onUnmounted(() => {
  document.removeEventListener("chart-bar-selected", handleChartBarSelected);
});
</script>
